import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type HoldingTransactionRow,
  useInventoryHoldingTransactions,
} from '../api/inventory';
import { useReleaseTransactionInventory } from '../api/transactions';
import { ApiError } from '../api/client';
import { Modal } from '../components/Modal';
import { Money } from '../components/Money';

export interface InventoryHoldsDrawerProps {
  eventId: string;
  product: { productId: string; name: string };
  onClose: () => void;
}

const STATUS_PILL: Record<HoldingTransactionRow['status'], string> = {
  pending: 'pill-draft',
  canceled: 'pill-declined',
  voided: 'pill-declined',
};

function tenderLabel(t: HoldingTransactionRow['tenderType']): string {
  switch (t) {
    case 'card_present':
      return 'Tap';
    case 'keyed':
      return 'Keyed';
    case 'cash':
      return 'Cash';
  }
}

/**
 * When-stamp for a held transaction. We pick the most informative timestamp
 * available: cancel time for canceled, void time for voided, otherwise the
 * created_at (pending). succeeded_at + canceled_at can co-exist on cash voids.
 */
function whenFor(row: HoldingTransactionRow): string {
  const ts = row.canceledAt ?? row.voidedAt ?? row.succeededAt ?? row.createdAt;
  return new Date(ts).toLocaleString();
}

export function InventoryHoldsDrawer({
  eventId,
  product,
  onClose,
}: InventoryHoldsDrawerProps) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useInventoryHoldingTransactions(
    eventId,
    product.productId,
  );
  const release = useReleaseTransactionInventory(eventId);
  const [busyTxn, setBusyTxn] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['event', eventId, 'inventory'] });
    qc.invalidateQueries({ queryKey: ['event', eventId, 'inventory-holds'] });
    qc.invalidateQueries({ queryKey: ['event', eventId, 'reports'] });
    qc.invalidateQueries({ queryKey: ['event', eventId, 'products'] });
  };

  const onReleaseOne = async (transactionId: string) => {
    setError(null);
    setBusyTxn(transactionId);
    try {
      await release.mutateAsync({
        transactionId,
        note: 'released from holds drawer',
      });
      invalidateAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t release; try again.');
    } finally {
      setBusyTxn(null);
    }
  };

  const onReleaseAll = async () => {
    if (!data || data.length === 0) return;
    if (
      !window.confirm(
        `Release inventory on all ${data.length} held transaction${data.length === 1 ? '' : 's'} for ${product.name}? This is idempotent — running again is safe.`,
      )
    ) {
      return;
    }
    setError(null);
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: data.length });
    try {
      // Sequential rather than Promise.all: keeps the inventory_movements
      // ledger ordered cleanly and avoids hammering the backend with N parallel
      // writes that each take a row lock on the same product.
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        await release.mutateAsync({
          transactionId: row.transactionId,
          note: 'bulk released from holds drawer',
        });
        setBulkProgress({ done: i + 1, total: data.length });
      }
      invalidateAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t release all; some may have succeeded. Try again.');
    } finally {
      setBulkBusy(false);
      setBulkProgress(null);
    }
  };

  const totalHeld = data?.reduce((acc, r) => acc + r.heldQuantity, 0) ?? 0;

  return (
    <Modal
      title="Held inventory"
      sub={product.name}
      width={640}
      onClose={onClose}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>
          These transactions held units that never became completed sales (pending,
          canceled, or voided). Release each to return the units to stock — release is
          idempotent, so running it more than once is safe.
        </div>

        {isLoading && <div style={{ color: 'var(--fg-3)' }}>Loading…</div>}
        {isError && (
          <div className="form-error">Couldn't load holding transactions.</div>
        )}
        {data && data.length === 0 && (
          <div style={{ color: 'var(--fg-3)' }}>No holds for this product.</div>
        )}

        {data && data.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13 }}>
                <strong>{totalHeld}</strong> unit{totalHeld === 1 ? '' : 's'} held across{' '}
                <strong>{data.length}</strong> transaction{data.length === 1 ? '' : 's'}.
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void onReleaseAll()}
                disabled={bulkBusy || busyTxn !== null}
              >
                {bulkBusy
                  ? `Releasing… ${bulkProgress?.done ?? 0}/${bulkProgress?.total ?? data.length}`
                  : 'Release all'}
              </button>
            </div>

            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Status</th>
                  <th>Tender</th>
                  <th>Card</th>
                  <th>Description</th>
                  <th className="num">Amount</th>
                  <th className="num">Held</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.transactionId}>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                      {whenFor(row)}
                    </td>
                    <td>
                      <span className={`pill ${STATUS_PILL[row.status]}`}>{row.status}</span>
                    </td>
                    <td>{tenderLabel(row.tenderType)}</td>
                    <td>
                      {row.cardBrand && row.cardLast4 ? (
                        <span style={{ color: 'var(--fg-3)' }}>
                          {row.cardBrand} ••{row.cardLast4}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td
                      style={{
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.description ?? ''}
                    </td>
                    <td className="num">
                      <Money value={row.amountCents / 100} />
                    </td>
                    <td className="num">{row.heldQuantity}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void onReleaseOne(row.transactionId)}
                        disabled={bulkBusy || busyTxn !== null}
                      >
                        {busyTxn === row.transactionId ? 'Releasing…' : 'Release'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {error && <div className="form-error">{error}</div>}
      </div>
    </Modal>
  );
}
